/**
 * Interactive map
 * @requires https://github.com/Leaflet/Leaflet
 */

/*
 * Named and callable, not an IIFE that runs once: a .interactive-map can
 * arrive after load. The consent facade keeps the map element inside a
 * <template> until it is released (docs/architecture/CONSENT_AND_PRIVACY.md §4.3), and
 * theme/assets/js/consent.js then calls window.booostInitMaps(parent) on
 * whatever it just inserted. Elements already initialised are skipped, so
 * calling it again over the whole document is harmless.
 */
/*
 * Skeleton (docs/features/PLACEHOLDERS.md §7, §13.4): leaflet.pt puts placeholder-glow
 * on .interactive-map and drops the `behind` macro inside it - a .placeholder
 * span filling the box plus a visually-hidden "Loading..." - so the box
 * shimmers until the map paints. Leaflet never removes what was in its
 * container, so this takes it away: on the tile layer's `load` (the visible
 * tiles are in), on `tileerror` (the map is up, the tiles are not coming -
 * shimmering on would promise what will not arrive), when Leaflet itself is
 * missing or throws, and on a timeout as the last resort. Whatever happens,
 * no skeleton shimmers forever. Idempotent: the first call wins.
 */
const SKELETON_TIMEOUT = 8000

window.booostClearMapSkeleton = function (el) {
  if (!el || !el.dataset || el.dataset.mapSkeletonCleared) return false
  el.dataset.mapSkeletonCleared = '1'
  el.classList.remove('placeholder-glow')
  el.removeAttribute('aria-busy')
  // Only the macro's own direct children: Leaflet's panes and controls live
  // in here too and must stay.
  const children = Array.prototype.slice.call(el.children || [])
  for (let c = 0; c < children.length; c++) {
    const cls = children[c].classList
    if (cls.contains('placeholder') || cls.contains('visually-hidden')) {
      el.removeChild(children[c])
    }
  }
  return true
}

window.booostInitMaps = function (root) {
  const mapList = (root || document).querySelectorAll('.interactive-map')

  if (mapList.length === 0) return

  for (let i = 0; i < mapList.length; i++) {
    if (mapList[i].dataset.mapInitialised) continue
    mapList[i].dataset.mapInitialised = '1'
    const el = mapList[i]
    const clear = () => window.booostClearMapSkeleton(el)

    if (typeof L === 'undefined') {
      // Leaflet not loaded: nothing will ever paint over the skeleton.
      clear()
      continue
    }
    const timer = setTimeout(clear, SKELETON_TIMEOUT)
    const done = () => {
      clearTimeout(timer)
      clear()
    }

    try {
      initMap(el, done)
    } catch (e) {
      done()
      throw e
    }
  }
}

/* eslint-disable no-undef */
function initMap(container, done) {
  const mapOptions = container.dataset.mapOptions
  let map
  let tiles

  // Map options: Inline JSON data
  if (mapOptions && mapOptions !== '') {
    const mapOptionsObj = JSON.parse(mapOptions),
      mapLayer =
        mapOptionsObj.mapLayer ||
        'https://api.maptiler.com/maps/pastel/{z}/{x}/{y}.png?key=BO4zZpr0fIIoydRTOLSx',
      mapCenter = mapOptionsObj.center ? mapOptionsObj.center : [0, 0],
      mapZoom = mapOptionsObj.zoom || 1,
      scrollWheelZoom = mapOptionsObj.scrollWheelZoom === false ? false : true,
      markers = mapOptionsObj.markers

    // Map setup
    map = L.map(container, {
      scrollWheelZoom: scrollWheelZoom,
    }).setView(mapCenter, mapZoom)

    // Tile layer
    tiles = L.tileLayer(mapLayer, {
      tileSize: 512,
      zoomOffset: -1,
      minZoom: 1,
      attribution:
        '\u003ca href="https://www.maptiler.com/copyright/" target="_blank"\u003e\u0026copy; MapTiler\u003c/a\u003e \u003ca href="https://www.openstreetmap.org/copyright" target="_blank"\u003e\u0026copy; OpenStreetMap contributors\u003c/a\u003e',
      crossOrigin: true,
    }).addTo(map)

    // Markers
    if (markers) {
      for (let n = 0; n < markers.length; n++) {
        const iconUrl = markers[n].iconUrl,
          shadowUrl = markers[n].shadowUrl,
          markerIcon = L.icon({
            iconUrl: iconUrl || 'assets/img/map/marker-icon.png',
            iconSize: [30, 43],
            iconAnchor: [14, 43],
            shadowUrl: shadowUrl || 'assets/img/map/marker-shadow.png',
            shadowSize: [41, 41],
            shadowAnchor: [13, 41],
            popupAnchor: [1, -40],
          }),
          popup = markers[n].popup

        const marker = L.marker(markers[n].position, {
          icon: markerIcon,
        }).addTo(map)

        if (popup) {
          marker.bindPopup(popup)
        }
      }
    }

    // Map option: No options provided
  } else {
    map = L.map(container).setView([0, 0], 1)

    tiles = L.tileLayer(
      'https://api.maptiler.com/maps/pastel/{z}/{x}/{y}.png?key=BO4zZpr0fIIoydRTOLSx',
      {
        tileSize: 512,
        zoomOffset: -1,
        minZoom: 1,
        attribution:
          '\u003ca href="https://www.maptiler.com/copyright/" target="_blank"\u003e\u0026copy; MapTiler\u003c/a\u003e \u003ca href="https://www.openstreetmap.org/copyright" target="_blank"\u003e\u0026copy; OpenStreetMap contributors\u003c/a\u003e',
        crossOrigin: true,
      }
    ).addTo(map)
  }

  // Ready path: the visible tiles are in. Failure path: a tile 404/timeout
  // still means the map is up and the skeleton is only in the way.
  tiles.on('load', done)
  tiles.on('tileerror', done)
}
/* eslint-enable no-undef */

export default window.booostInitMaps()
